/**
 * Minimal Google API payload subsets (OAuth, Docs, Sheets, Drive, Gmail) used by the desktop
 * Google client AND the sim. Objects are `.loose()`; only the fields the app reads are typed.
 */
import { z } from 'zod';

// ---------------------------------------------------------------------------------------------
// OAuth
// ---------------------------------------------------------------------------------------------

export const GOOGLE_SCOPES = {
  driveFile: 'https://www.googleapis.com/auth/drive.file',
  documents: 'https://www.googleapis.com/auth/documents',
  spreadsheets: 'https://www.googleapis.com/auth/spreadsheets',
  gmailCompose: 'https://www.googleapis.com/auth/gmail.compose',
  userinfoEmail: 'https://www.googleapis.com/auth/userinfo.email',
  openid: 'openid',
} as const;
export type GoogleScope = (typeof GOOGLE_SCOPES)[keyof typeof GOOGLE_SCOPES];

export const GoogleTokenResponseSchema = z
  .object({
    access_token: z.string().min(1),
    expires_in: z.number().int().positive(),
    refresh_token: z.string().optional(),
    scope: z.string(),
    token_type: z.literal('Bearer'),
    id_token: z.string().optional(),
  })
  .loose();
export type GoogleTokenResponse = z.infer<typeof GoogleTokenResponseSchema>;

export const GoogleTokenErrorSchema = z
  .object({
    error: z.string(),
    error_description: z.string().optional(),
  })
  .loose();
export type GoogleTokenError = z.infer<typeof GoogleTokenErrorSchema>;

export const GoogleUserinfoSchema = z
  .object({
    sub: z.string(),
    email: z.string(),
    email_verified: z.boolean().optional(),
    name: z.string().optional(),
    hd: z.string().optional(),
  })
  .loose();
export type GoogleUserinfo = z.infer<typeof GoogleUserinfoSchema>;

/** Standard Google JSON error envelope. */
export const GoogleApiErrorSchema = z
  .object({
    error: z
      .object({
        code: z.number().int(),
        message: z.string(),
        status: z.string().optional(),
        errors: z.array(z.object({ reason: z.string().optional(), message: z.string().optional() }).loose()).optional(),
      })
      .loose(),
  })
  .loose();
export type GoogleApiError = z.infer<typeof GoogleApiErrorSchema>;

// ---------------------------------------------------------------------------------------------
// Docs
// ---------------------------------------------------------------------------------------------

export const DocsLocationSchema = z.object({ index: z.number().int().nonnegative(), segmentId: z.string().optional(), tabId: z.string().optional() });
export const DocsRangeSchema = z.object({
  startIndex: z.number().int().nonnegative(),
  endIndex: z.number().int().nonnegative(),
  segmentId: z.string().optional(),
  tabId: z.string().optional(),
});
export type DocsRange = z.infer<typeof DocsRangeSchema>;

export const DocsLinkSchema = z.object({ url: z.string().url() }).or(z.object({ headingId: z.string() })).or(z.object({ bookmarkId: z.string() }));

export const DocsTextStyleSchema = z
  .object({
    bold: z.boolean().optional(),
    italic: z.boolean().optional(),
    underline: z.boolean().optional(),
    strikethrough: z.boolean().optional(),
    link: DocsLinkSchema.optional(),
    fontSize: z.object({ magnitude: z.number(), unit: z.literal('PT') }).optional(),
    weightedFontFamily: z.object({ fontFamily: z.string(), weight: z.number().int().optional() }).optional(),
  })
  .loose();
export type DocsTextStyle = z.infer<typeof DocsTextStyleSchema>;

export const DOCS_NAMED_STYLES = [
  'NORMAL_TEXT',
  'TITLE',
  'SUBTITLE',
  'HEADING_1',
  'HEADING_2',
  'HEADING_3',
  'HEADING_4',
  'HEADING_5',
  'HEADING_6',
] as const;
export const DocsNamedStyleTypeSchema = z.enum(DOCS_NAMED_STYLES);

export const DocsParagraphStyleSchema = z
  .object({
    namedStyleType: DocsNamedStyleTypeSchema.optional(),
    headingId: z.string().optional(),
    alignment: z.enum(['START', 'CENTER', 'END', 'JUSTIFIED']).optional(),
    spaceAbove: z.object({ magnitude: z.number(), unit: z.literal('PT') }).optional(),
    spaceBelow: z.object({ magnitude: z.number(), unit: z.literal('PT') }).optional(),
  })
  .loose();

export const DocsParagraphElementSchema = z
  .object({
    startIndex: z.number().int().nonnegative().optional(),
    endIndex: z.number().int().nonnegative(),
    textRun: z.object({ content: z.string(), textStyle: DocsTextStyleSchema.optional() }).loose().optional(),
    inlineObjectElement: z.object({ inlineObjectId: z.string() }).loose().optional(),
    pageBreak: z.object({}).loose().optional(),
    horizontalRule: z.object({}).loose().optional(),
  })
  .loose();
export type DocsParagraphElement = z.infer<typeof DocsParagraphElementSchema>;

export const DocsBulletSchema = z.object({ listId: z.string(), nestingLevel: z.number().int().nonnegative().optional() }).loose();

export const DocsStructuralElementSchema = z
  .object({
    startIndex: z.number().int().nonnegative().optional(),
    endIndex: z.number().int().nonnegative(),
    paragraph: z
      .object({
        elements: z.array(DocsParagraphElementSchema),
        paragraphStyle: DocsParagraphStyleSchema.optional(),
        bullet: DocsBulletSchema.optional(),
      })
      .loose()
      .optional(),
    sectionBreak: z.object({}).loose().optional(),
    table: z.object({ rows: z.number().int(), columns: z.number().int() }).loose().optional(),
    tableOfContents: z.object({}).loose().optional(),
  })
  .loose();
export type DocsStructuralElement = z.infer<typeof DocsStructuralElementSchema>;

export const DocsNamedRangeSchema = z.object({
  namedRangeId: z.string(),
  name: z.string(),
  ranges: z.array(DocsRangeSchema),
});
export type DocsNamedRange = z.infer<typeof DocsNamedRangeSchema>;

export const DocsNamedRangesMapSchema = z.record(z.string(), z.object({ namedRanges: z.array(DocsNamedRangeSchema) }));

/** Document tabs (template feedback 2026-09-25): `documentTab` is present only with `includeTabsContent=true`. */
export const DocsTabPropertiesSchema = z
  .object({ tabId: z.string(), title: z.string().optional(), index: z.number().int().optional(), parentTabId: z.string().optional(), nestingLevel: z.number().int().optional() })
  .loose();
export type DocsTabProperties = z.infer<typeof DocsTabPropertiesSchema>;
export const DocsDocumentTabSchema = z
  .object({
    body: z.object({ content: z.array(DocsStructuralElementSchema) }).loose(),
    namedRanges: DocsNamedRangesMapSchema.optional(),
    lists: z.record(z.string(), z.unknown()).optional(),
  })
  .loose();
export const DocsTabSchema = z.object({ tabProperties: DocsTabPropertiesSchema, documentTab: DocsDocumentTabSchema.optional(), childTabs: z.array(z.unknown()).optional() }).loose();

export const DocsDocumentSchema = z
  .object({
    documentId: z.string(),
    title: z.string(),
    // real-google change: Google populates revisionId only when the user has edit access.
    revisionId: z.string().optional(),
    body: z.object({ content: z.array(DocsStructuralElementSchema) }).loose(),
    namedRanges: DocsNamedRangesMapSchema.optional(),
    lists: z.record(z.string(), z.unknown()).optional(),
    tabs: z.array(DocsTabSchema).optional(),
    suggestionsViewMode: z.string().optional(),
  })
  .loose();
export type DocsDocument = z.infer<typeof DocsDocumentSchema>;

/**
 * documents.get with `includeTabsContent=true` returns each tab's content under
 * `tabs[].documentTab` and leaves the document-level `body` / `namedRanges` unset (verified on real
 * Google Docs, 2026-09-29; the sim returned both). The app reads the legacy fields as the first
 * tab, so they are filled from it before parsing. A response that already carries `body` is kept.
 */
export function withFirstTabAsBody(raw: unknown): unknown {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return raw;
  const doc = raw as Record<string, unknown>;
  if (doc.body !== undefined) return raw;
  const tabs = Array.isArray(doc.tabs) ? doc.tabs : [];
  const first = tabs[0] as { documentTab?: { body?: unknown; namedRanges?: unknown } } | undefined;
  if (!first?.documentTab?.body) return raw;
  return { ...doc, body: first.documentTab.body, ...(doc.namedRanges === undefined && first.documentTab.namedRanges !== undefined ? { namedRanges: first.documentTab.namedRanges } : {}) };
}
export type DocsTab = z.infer<typeof DocsTabSchema>;

export const DocsCreateRequestSchema = z.object({ title: z.string().min(1) });

export const DOCS_BULLET_PRESETS = [
  'BULLET_DISC_CIRCLE_SQUARE',
  'BULLET_DIAMONDX_ARROW3D_SQUARE',
  'BULLET_CHECKBOX',
  'BULLET_ARROW_DIAMOND_DISC',
  'NUMBERED_DECIMAL_ALPHA_ROMAN',
  'NUMBERED_DECIMAL_NESTED',
] as const;

/** Tabs a request applies to (deleteNamedRange); omitted means every tab. */
export const DocsTabsCriteriaSchema = z.object({ tabIds: z.array(z.string()) });

export const DocsRequestSchema = z.union([
  z.object({ insertText: z.object({ text: z.string(), location: DocsLocationSchema.optional(), endOfSegmentLocation: z.object({ segmentId: z.string().optional(), tabId: z.string().optional() }).optional() }) }),
  z.object({ deleteContentRange: z.object({ range: DocsRangeSchema }) }),
  z.object({ updateParagraphStyle: z.object({ range: DocsRangeSchema, paragraphStyle: DocsParagraphStyleSchema, fields: z.string() }) }),
  z.object({ createParagraphBullets: z.object({ range: DocsRangeSchema, bulletPreset: z.enum(DOCS_BULLET_PRESETS) }) }),
  z.object({ deleteParagraphBullets: z.object({ range: DocsRangeSchema }) }),
  z.object({ updateTextStyle: z.object({ range: DocsRangeSchema, textStyle: DocsTextStyleSchema, fields: z.string() }) }),
  z.object({ createNamedRange: z.object({ name: z.string().min(1), range: DocsRangeSchema }) }),
  z.object({ deleteNamedRange: z.union([z.object({ namedRangeId: z.string(), tabsCriteria: DocsTabsCriteriaSchema.optional() }), z.object({ name: z.string(), tabsCriteria: DocsTabsCriteriaSchema.optional() })]) }),
  z.object({ replaceAllText: z.object({ containsText: z.object({ text: z.string(), matchCase: z.boolean().optional() }), replaceText: z.string() }) }),
  z.object({ insertPageBreak: z.object({ location: DocsLocationSchema }) }),
  // Document tabs (AddDocumentTabRequest, UpdateDocumentTabPropertiesRequest, DeleteTabRequest).
  z.object({ addDocumentTab: z.object({ tabProperties: z.object({ title: z.string().optional(), index: z.number().int().nonnegative().optional(), parentTabId: z.string().optional() }).optional() }) }),
  z.object({ updateDocumentTabProperties: z.object({ tabProperties: z.object({ tabId: z.string(), title: z.string().optional(), index: z.number().int().nonnegative().optional() }), fields: z.string() }) }),
  z.object({ deleteTab: z.object({ tabId: z.string() }) }),
]);
export type DocsRequest = z.infer<typeof DocsRequestSchema>;

export const DocsWriteControlSchema = z.object({
  requiredRevisionId: z.string().optional(),
  targetRevisionId: z.string().optional(),
});
export type DocsWriteControl = z.infer<typeof DocsWriteControlSchema>;

export const DocsBatchUpdateRequestSchema = z.object({
  requests: z.array(DocsRequestSchema).min(1),
  writeControl: DocsWriteControlSchema.optional(),
});
export type DocsBatchUpdateRequest = z.infer<typeof DocsBatchUpdateRequestSchema>;

export const DocsBatchUpdateResponseSchema = z
  .object({
    documentId: z.string(),
    replies: z.array(z.record(z.string(), z.unknown())).default([]),
    writeControl: z.object({ requiredRevisionId: z.string() }).optional(),
  })
  .loose();
export type DocsBatchUpdateResponse = z.infer<typeof DocsBatchUpdateResponseSchema>;

/**
 * Managed regions: named ranges written by Agency, plus the legacy marker pairs imported for
 * compatibility (spec section 8). `asks_overflow` lives on the Additional asks tab (template
 * feedback 2026-09-25) and only ever exists as a named range.
 */
export const AGENDA_MARKERS = {
  asks: { start: 'VANTA_ASKS_START', end: 'VANTA_ASKS_END', namedRange: 'agency.asks' },
  frameworks: { start: 'VANTA_FRAMEWORKS_START', end: 'VANTA_FRAMEWORKS_END', namedRange: 'agency.frameworks' },
  asks_overflow: { start: 'AGENCY_ASKS_OVERFLOW_START', end: 'AGENCY_ASKS_OVERFLOW_END', namedRange: 'agency.asks_overflow' },
} as const;

/** Named range around the meeting line so an update can rewrite it wherever the operator moved it. */
export const AGENDA_MEETING_NAMED_RANGE = 'agency.meeting';

// ---------------------------------------------------------------------------------------------
// Sheets
// ---------------------------------------------------------------------------------------------

export const SheetsGridPropertiesSchema = z.object({
  rowCount: z.number().int().nonnegative().optional(),
  columnCount: z.number().int().nonnegative().optional(),
  frozenRowCount: z.number().int().nonnegative().optional(),
  frozenColumnCount: z.number().int().nonnegative().optional(),
}).loose();

export const SheetsSheetPropertiesSchema = z
  .object({
    sheetId: z.number().int(),
    title: z.string(),
    index: z.number().int().nonnegative().optional(),
    sheetType: z.enum(['GRID', 'OBJECT']).optional(),
    gridProperties: SheetsGridPropertiesSchema.optional(),
    hidden: z.boolean().optional(),
  })
  .loose();
export type SheetsSheetProperties = z.infer<typeof SheetsSheetPropertiesSchema>;

// real-google change: `Sheet.tables[]` as returned by spreadsheets.get (used to update instead of re-add a table).
export const SheetsTableSchema = z
  .object({
    tableId: z.string().optional(),
    name: z.string().optional(),
    range: z.object({ sheetId: z.number().int().optional(), startRowIndex: z.number().int().optional(), endRowIndex: z.number().int().optional(), startColumnIndex: z.number().int().optional(), endColumnIndex: z.number().int().optional() }).loose().optional(),
  })
  .loose();
export type SheetsTable = z.infer<typeof SheetsTableSchema>;

/** Only the grid fields needed to identify previously generated rows; unknown Google fields survive. */
export const SheetsGridCellSchema = z.object({
  userEnteredValue: z.object({ stringValue: z.string().optional(), numberValue: z.number().optional(), boolValue: z.boolean().optional(), formulaValue: z.string().optional() }).optional(),
  dataValidation: z.object({ condition: z.object({ type: z.string(), values: z.array(z.object({ userEnteredValue: z.string().optional() }).loose()).optional() }).loose() }).loose().optional(),
  note: z.string().optional(),
}).loose();
export type SheetsGridCell = z.infer<typeof SheetsGridCellSchema>;

export const SheetsSpreadsheetSchema = z
  .object({
    spreadsheetId: z.string(),
    properties: z.object({ title: z.string(), locale: z.string().optional(), timeZone: z.string().optional() }).loose(),
    sheets: z.array(z.object({
      properties: SheetsSheetPropertiesSchema,
      tables: z.array(SheetsTableSchema).optional(),
      basicFilter: z.object({ range: SheetsTableSchema.shape.range }).loose().optional(),
      data: z.array(z.object({ startRow: z.number().int().optional(), startColumn: z.number().int().optional(), rowData: z.array(z.object({ values: z.array(SheetsGridCellSchema).optional() })).optional() })).optional(),
    }).loose()).default([]),
    spreadsheetUrl: z.string().url().optional(),
  })
  .loose();
export type SheetsSpreadsheet = z.infer<typeof SheetsSpreadsheetSchema>;

export const SheetsCreateRequestSchema = z.object({
  properties: z.object({ title: z.string().min(1), timeZone: z.string().optional() }),
  sheets: z.array(z.object({ properties: SheetsSheetPropertiesSchema.partial() })).optional(),
});

export const SheetsCellValueSchema = z.union([z.string(), z.number(), z.boolean(), z.null()]);
export type SheetsCellValue = z.infer<typeof SheetsCellValueSchema>;

export const SheetsValueRangeSchema = z.object({
  range: z.string(),
  majorDimension: z.enum(['ROWS', 'COLUMNS']).default('ROWS'),
  values: z.array(z.array(SheetsCellValueSchema)).default([]),
});
export type SheetsValueRange = z.infer<typeof SheetsValueRangeSchema>;

export const SheetsValuesBatchUpdateRequestSchema = z.object({
  valueInputOption: z.enum(['RAW', 'USER_ENTERED']),
  data: z.array(SheetsValueRangeSchema).min(1),
  includeValuesInResponse: z.boolean().optional(),
});
export type SheetsValuesBatchUpdateRequest = z.infer<typeof SheetsValuesBatchUpdateRequestSchema>;

export const SheetsValuesBatchUpdateResponseSchema = z
  .object({
    spreadsheetId: z.string(),
    totalUpdatedRows: z.number().int().nonnegative().optional(),
    totalUpdatedColumns: z.number().int().nonnegative().optional(),
    totalUpdatedCells: z.number().int().nonnegative().optional(),
    totalUpdatedSheets: z.number().int().nonnegative().optional(),
    responses: z.array(z.record(z.string(), z.unknown())).optional(),
  })
  .loose();
export type SheetsValuesBatchUpdateResponse = z.infer<typeof SheetsValuesBatchUpdateResponseSchema>;

export const SheetsValuesClearRequestSchema = z.object({}).loose();

export const SheetsGridRangeSchema = z.object({
  sheetId: z.number().int(),
  startRowIndex: z.number().int().nonnegative().optional(),
  endRowIndex: z.number().int().nonnegative().optional(),
  startColumnIndex: z.number().int().nonnegative().optional(),
  endColumnIndex: z.number().int().nonnegative().optional(),
});
export type SheetsGridRange = z.infer<typeof SheetsGridRangeSchema>;

export const SheetsColorSchema = z.object({
  red: z.number().min(0).max(1).optional(),
  green: z.number().min(0).max(1).optional(),
  blue: z.number().min(0).max(1).optional(),
  alpha: z.number().min(0).max(1).optional(),
});

export const SheetsCellFormatSchema = z
  .object({
    backgroundColor: SheetsColorSchema.optional(),
    textFormat: z
      .object({
        bold: z.boolean().optional(),
        italic: z.boolean().optional(),
        fontSize: z.number().int().optional(),
        fontFamily: z.string().optional(),
        foregroundColor: SheetsColorSchema.optional(),
        /** Whole-cell hyperlink (the Vulnerabilities sheet's Asset column links to the finding). */
        link: z.object({ uri: z.string() }).optional(),
      })
      .loose()
      .optional(),
    wrapStrategy: z.enum(['OVERFLOW_CELL', 'LEGACY_WRAP', 'CLIP', 'WRAP']).optional(),
    verticalAlignment: z.enum(['TOP', 'MIDDLE', 'BOTTOM']).optional(),
    horizontalAlignment: z.enum(['LEFT', 'CENTER', 'RIGHT']).optional(),
  })
  .loose();

export const SheetsDataValidationRuleSchema = z.object({
  condition: z.object({
    type: z.enum(['ONE_OF_LIST', 'BOOLEAN', 'ONE_OF_RANGE', 'TEXT_IS_EMAIL', 'DATE_IS_VALID', 'CUSTOM_FORMULA']),
    values: z.array(z.object({ userEnteredValue: z.string() })).optional(),
  }),
  strict: z.boolean().optional(),
  showCustomUi: z.boolean().optional(),
  inputMessage: z.string().optional(),
});

export const SheetsRequestSchema = z.union([
  z.object({ addSheet: z.object({ properties: SheetsSheetPropertiesSchema.partial() }) }),
  z.object({ deleteSheet: z.object({ sheetId: z.number().int() }) }),
  z.object({ updateSheetProperties: z.object({ properties: SheetsSheetPropertiesSchema.partial(), fields: z.string() }) }),
  z.object({ repeatCell: z.object({ range: SheetsGridRangeSchema, cell: z.object({ userEnteredFormat: SheetsCellFormatSchema.optional(), dataValidation: SheetsDataValidationRuleSchema.optional() }), fields: z.string() }) }),
  z.object({ setDataValidation: z.object({ range: SheetsGridRangeSchema, rule: SheetsDataValidationRuleSchema.optional() }) }),
  z.object({ updateDimensionProperties: z.object({ range: z.object({ sheetId: z.number().int(), dimension: z.enum(['ROWS', 'COLUMNS']), startIndex: z.number().int().nonnegative().optional(), endIndex: z.number().int().nonnegative().optional() }), properties: z.object({ pixelSize: z.number().int().positive().optional(), hiddenByUser: z.boolean().optional() }), fields: z.string() }) }),
  z.object({ autoResizeDimensions: z.object({ dimensions: z.object({ sheetId: z.number().int(), dimension: z.enum(['ROWS', 'COLUMNS']), startIndex: z.number().int().nonnegative().optional(), endIndex: z.number().int().nonnegative().optional() }) }) }),
  z.object({ setBasicFilter: z.object({ filter: z.object({ range: SheetsGridRangeSchema }) }) }),
  z.object({ clearBasicFilter: z.object({ sheetId: z.number().int() }) }),
  z.object({ addProtectedRange: z.object({ protectedRange: z.object({ range: SheetsGridRangeSchema, description: z.string().optional(), warningOnly: z.boolean().optional() }) }) }),
  z.object({ addTable: z.object({ table: z.object({ name: z.string(), tableId: z.string().optional(), range: SheetsGridRangeSchema, columnProperties: z.array(z.object({ columnIndex: z.number().int(), columnName: z.string(), columnType: z.string().optional(), dataValidationRule: SheetsDataValidationRuleSchema.optional() })).optional() }).loose() }) }),
  // real-google change: UpdateTableRequest / DeleteTableRequest (Sheets API discovery 20260916); republish updates the existing table.
  z.object({ updateTable: z.object({ table: z.object({ tableId: z.string(), name: z.string().optional(), range: SheetsGridRangeSchema.optional(), columnProperties: z.array(z.object({ columnIndex: z.number().int(), columnName: z.string(), columnType: z.string().optional(), dataValidationRule: SheetsDataValidationRuleSchema.optional() })).optional() }).loose(), fields: z.string() }) }),
  z.object({ deleteTable: z.object({ tableId: z.string() }) }),
  z.object({ updateCells: z.object({ range: SheetsGridRangeSchema, rows: z.array(z.object({ values: z.array(z.object({ userEnteredValue: z.object({ stringValue: z.string().optional(), numberValue: z.number().optional(), boolValue: z.boolean().optional(), formulaValue: z.string().optional() }).optional(), userEnteredFormat: SheetsCellFormatSchema.optional(), note: z.string().optional() })) })), fields: z.string() }) }),
  z.object({ appendDimension: z.object({ sheetId: z.number().int(), dimension: z.enum(['ROWS', 'COLUMNS']), length: z.number().int().positive() }) }),
]);
export type SheetsRequest = z.infer<typeof SheetsRequestSchema>;

export const SheetsBatchUpdateRequestSchema = z.object({
  requests: z.array(SheetsRequestSchema).min(1),
  includeSpreadsheetInResponse: z.boolean().optional(),
});
export type SheetsBatchUpdateRequest = z.infer<typeof SheetsBatchUpdateRequestSchema>;

export const SheetsBatchUpdateResponseSchema = z
  .object({
    spreadsheetId: z.string(),
    replies: z.array(z.record(z.string(), z.unknown())).default([]),
    updatedSpreadsheet: SheetsSpreadsheetSchema.optional(),
  })
  .loose();
export type SheetsBatchUpdateResponse = z.infer<typeof SheetsBatchUpdateResponseSchema>;

// ---------------------------------------------------------------------------------------------
// Drive
// ---------------------------------------------------------------------------------------------

export const GOOGLE_MIME = {
  document: 'application/vnd.google-apps.document',
  spreadsheet: 'application/vnd.google-apps.spreadsheet',
  folder: 'application/vnd.google-apps.folder',
} as const;

export const DriveFileSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    mimeType: z.string(),
    webViewLink: z.string().url().optional(),
    parents: z.array(z.string()).optional(),
    trashed: z.boolean().optional(),
    createdTime: z.iso.datetime({ offset: true }).optional(),
    modifiedTime: z.iso.datetime({ offset: true }).optional(),
    owners: z.array(z.object({ emailAddress: z.string().optional(), displayName: z.string().optional() }).loose()).optional(),
    capabilities: z.object({ canEdit: z.boolean().optional(), canTrash: z.boolean().optional() }).loose().optional(),
  })
  .loose();
export type DriveFile = z.infer<typeof DriveFileSchema>;

export const DriveFileListSchema = z
  .object({
    files: z.array(DriveFileSchema),
    nextPageToken: z.string().optional(),
    incompleteSearch: z.boolean().optional(),
  })
  .loose();
export type DriveFileList = z.infer<typeof DriveFileListSchema>;

export const DriveCreateFileRequestSchema = z.object({
  name: z.string().min(1),
  mimeType: z.string().min(1),
  parents: z.array(z.string()).optional(),
});
export type DriveCreateFileRequest = z.infer<typeof DriveCreateFileRequestSchema>;

export const DriveUpdateFileRequestSchema = z.object({
  name: z.string().min(1).optional(),
  trashed: z.boolean().optional(),
});

// ---------------------------------------------------------------------------------------------
// Gmail
// ---------------------------------------------------------------------------------------------

export const GmailMessageSchema = z
  .object({
    id: z.string(),
    threadId: z.string(),
    labelIds: z.array(z.string()).optional(),
    snippet: z.string().optional(),
    historyId: z.string().optional(),
    internalDate: z.string().optional(),
    sizeEstimate: z.number().int().optional(),
    raw: z.string().optional(),
    payload: z
      .object({
        headers: z.array(z.object({ name: z.string(), value: z.string() })).optional(),
        mimeType: z.string().optional(),
      })
      .loose()
      .optional(),
  })
  .loose();
export type GmailMessage = z.infer<typeof GmailMessageSchema>;

export const GmailDraftSchema = z
  .object({
    id: z.string(),
    message: GmailMessageSchema.partial({ threadId: true }),
  })
  .loose();
export type GmailDraft = z.infer<typeof GmailDraftSchema>;

/** `raw` is the base64url encoded RFC 2822 message. */
export const GmailCreateDraftRequestSchema = z.object({
  message: z.object({ raw: z.string().min(1), threadId: z.string().optional() }),
});
export type GmailCreateDraftRequest = z.infer<typeof GmailCreateDraftRequestSchema>;

export const GmailSendMessageRequestSchema = z.object({
  raw: z.string().min(1),
  threadId: z.string().optional(),
});
export type GmailSendMessageRequest = z.infer<typeof GmailSendMessageRequestSchema>;

export const GmailSendDraftRequestSchema = z.object({ id: z.string().min(1) });

export const GmailDraftListSchema = z
  .object({
    drafts: z.array(z.object({ id: z.string(), message: z.object({ id: z.string(), threadId: z.string().optional() }).loose() })).optional(),
    nextPageToken: z.string().optional(),
    resultSizeEstimate: z.number().int().optional(),
  })
  .loose();

export const GmailProfileSchema = z
  .object({
    emailAddress: z.string(),
    messagesTotal: z.number().int().optional(),
    threadsTotal: z.number().int().optional(),
    historyId: z.string().optional(),
  })
  .loose();
export type GmailProfile = z.infer<typeof GmailProfileSchema>;

export const GmailSendAsSchema = z
  .object({
    sendAsEmail: z.string(),
    displayName: z.string().optional(),
    isPrimary: z.boolean().optional(),
    isDefault: z.boolean().optional(),
    verificationStatus: z.enum(['accepted', 'pending', 'verificationStatusUnspecified']).optional(),
  })
  .loose();
export const GmailSendAsListSchema = z.object({ sendAs: z.array(GmailSendAsSchema) }).loose();
export type GmailSendAs = z.infer<typeof GmailSendAsSchema>;

/** Endpoint templates relative to the Google API base URL(s). */
export const GOOGLE_ENDPOINTS = {
  oauthAuthorize: '/o/oauth2/v2/auth',
  oauthToken: '/oauth2/token',
  oauthRevoke: '/oauth2/revoke',
  userinfo: '/oauth2/v3/userinfo',
  driveFiles: '/drive/v3/files',
  driveFile: '/drive/v3/files/{fileId}',
  docsDocuments: '/docs/v1/documents',
  docsDocument: '/docs/v1/documents/{documentId}',
  docsBatchUpdate: '/docs/v1/documents/{documentId}:batchUpdate',
  sheetsSpreadsheets: '/sheets/v4/spreadsheets',
  sheetsSpreadsheet: '/sheets/v4/spreadsheets/{spreadsheetId}',
  sheetsBatchUpdate: '/sheets/v4/spreadsheets/{spreadsheetId}:batchUpdate',
  sheetsValuesBatchUpdate: '/sheets/v4/spreadsheets/{spreadsheetId}/values:batchUpdate',
  // real-google change
  sheetsValuesBatchClear: '/sheets/v4/spreadsheets/{spreadsheetId}/values:batchClear',
  sheetsValuesGet: '/sheets/v4/spreadsheets/{spreadsheetId}/values/{range}',
  gmailDrafts: '/gmail/v1/users/me/drafts',
  gmailDraft: '/gmail/v1/users/me/drafts/{draftId}',
  gmailDraftSend: '/gmail/v1/users/me/drafts/send',
  gmailMessagesSend: '/gmail/v1/users/me/messages/send',
  gmailMessage: '/gmail/v1/users/me/messages/{messageId}',
  gmailProfile: '/gmail/v1/users/me/profile',
  gmailSendAs: '/gmail/v1/users/me/settings/sendAs',
} as const;
